import { useCallback, useEffect, useRef, useState } from "react";
import {
  Building2,
  CreditCard,
  FileText,
  ImageIcon,
  Receipt,
  Save,
  Upload,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { ChangePasswordCard } from "./ChangePasswordCard";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-blue-50 text-blue-700",
  unpaid: "bg-slate-100 text-slate-600",
  failed: "bg-red-50 text-red-700",
  expired: "bg-slate-100 text-slate-600",
  refunded: "bg-amber-50 text-amber-700",
};

const FIELDS = [
  ["name", "Vendor/Supplier name", "text", true],
  ["contact_name", "Primary contact", "text", false],
  ["contact_email", "Billing / contact email", "email", false],
  ["phone", "Phone number", "tel", false],
  ["website", "Website", "text", false],
  ["tax_id", "Tax ID / EIN", "text", false],
  ["credit_terms", "Default credit terms offered", "text", false],
];

const Card = ({ title, icon: Icon, children, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
      <Icon className="h-3.5 w-3.5 text-blue-600" /> {title}
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

export const CompanyProfile = ({ plans, onChanged }) => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState("");
  const logoRef = useRef(null);
  const w9Ref = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/company/profile");
    setProfile(data);
    setForm({
      name: data.name || "",
      contact_name: data.contact_name || "",
      contact_email: data.contact_email || "",
      phone: data.phone || "",
      website: data.website || "",
      address: data.address || "",
      tax_id: data.tax_id || "",
      credit_terms: data.credit_terms || "",
    });
    if (data.has_logo) {
      try {
        const res = await api.get("/logo?scope=own", { responseType: "blob" });
        setLogoUrl(URL.createObjectURL(res.data));
      } catch (e) {
        setLogoUrl(null);
      }
    } else {
      setLogoUrl(null);
    }
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/company/profile", form);
      toast.success("Profile saved");
      await load();
      await onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/company/logo", fd);
      toast.success("Logo updated — it now appears on your credit packets");
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    if (logoRef.current) logoRef.current.value = "";
  };

  const uploadW9 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/company/w9", fd);
      toast.success("W-9 uploaded");
      await load();
      await onChanged?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
    if (w9Ref.current) w9Ref.current.value = "";
  };

  const openW9 = async () => {
    try {
      const { data } = await api.get("/w9?scope=own", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const pay = async (lookup_key) => {
    setPaying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        company_id: profile.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errMsg(err));
      setPaying("");
    }
  };

  if (!profile || !form)
    return <div className="py-16 text-center text-sm text-slate-500">Loading profile…</div>;

  const billing = profile.billing || { charges: [] };

  return (
    <div className="space-y-6" data-testid="company-profile">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Vendor/Supplier logo" icon={ImageIcon} testId="profile-logo-card">
          <div className="flex items-center gap-5">
            <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Company logo"
                  className="h-full w-full object-contain p-1.5"
                  data-testid="profile-logo-image"
                />
              ) : (
                <Building2 className="h-7 w-7 text-slate-300" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm text-slate-600">
                {profile.has_logo
                  ? profile.logo_filename
                  : "PNG, JPG or WEBP up to 4MB. Shown on your credit packets."}
              </p>
              <input
                ref={logoRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={uploadLogo}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                size="sm"
                onClick={() => logoRef.current?.click()}
                data-testid="upload-logo-button"
                className="mt-3 rounded-full bg-slate-900 px-5 font-semibold transition-colors duration-200 hover:bg-slate-800 active:scale-95"
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                {profile.has_logo ? "Replace logo" : "Upload logo"}
              </Button>
            </div>
          </div>
        </Card>

        <Card title="Vendor/Supplier W-9" icon={FileText} testId="profile-w9-card">
          <p className="text-sm text-slate-600" data-testid="profile-w9-status">
            {profile.has_w9
              ? `${profile.w9_filename} — shared with every customer and attached to packets.`
              : "No W-9 on file. Upload a PDF so your customers can view or download it."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              ref={w9Ref}
              type="file"
              accept="application/pdf"
              onChange={uploadW9}
              className="hidden"
              data-testid="profile-w9-file-input"
            />
            <Button
              size="sm"
              onClick={() => w9Ref.current?.click()}
              data-testid="profile-upload-w9-button"
              className="rounded-full bg-slate-900 px-5 font-semibold transition-colors duration-200 hover:bg-slate-800 active:scale-95"
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" /> {profile.has_w9 ? "Replace" : "Upload"}
            </Button>
            {profile.has_w9 && (
              <Button
                variant="outline"
                size="sm"
                onClick={openW9}
                data-testid="profile-view-w9-button"
                className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
              >
                View
              </Button>
            )}
          </div>
        </Card>

        <Card title="Account at a glance" icon={Users} testId="profile-stats-card">
          <dl className="grid grid-cols-2 gap-5">
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Customers
              </dt>
              <dd
                className="mt-1 font-display text-2xl font-black text-slate-900"
                data-testid="profile-customer-count"
              >
                {profile.customers}
              </dd>
            </div>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                References
              </dt>
              <dd className="mt-1 font-display text-2xl font-black text-slate-900">
                {profile.references}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Login email
              </dt>
              <dd className="mt-1 truncate text-sm text-slate-800">{profile.login_email}</dd>
            </div>
          </dl>
        </Card>
      </div>

      <Card title="Subscription & payment options" icon={CreditCard} testId="profile-plan-card">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <div className="font-display text-2xl font-black tracking-tight text-slate-900">
              {billing.plan_name || "No plan selected"}
            </div>
            <div className="mt-1 text-sm text-slate-500">
              {billing.paid_at ? `Active since ${fmtDate(billing.paid_at)}` : "Not billed yet"}
            </div>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              STATUS_STYLE[billing.payment_status] || "bg-slate-100 text-slate-600"
            }`}
            data-testid="profile-payment-status"
          >
            {billing.payment_status}
          </span>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {plans.map((p) => {
            const current = billing.plan === p.lookup_key && billing.payment_status === "paid";
            return (
              <button
                key={p.lookup_key}
                type="button"
                disabled={!!paying}
                onClick={() => pay(p.lookup_key)}
                data-testid={`profile-plan-${p.lookup_key}`}
                className={`rounded-lg border p-5 text-left transition-colors duration-200 disabled:opacity-60 ${
                  current
                    ? "border-blue-600 bg-blue-50/60"
                    : "border-slate-200 bg-white hover:border-blue-500 hover:bg-blue-50/30"
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-display text-base font-bold text-slate-900">
                    {p.name} Plan
                  </span>
                  <span className="font-display text-lg font-black text-slate-900">{p.price}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{p.blurb}</div>
                <ul className="mt-3 space-y-1.5">
                  {(p.features || []).map((f) => (
                    <li key={f} className="text-xs text-slate-600">
                      · {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-xs font-semibold text-blue-600">
                  {current ? "Current plan" : `Subscribe · per ${p.interval}`}
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Secure checkout by Stripe · test card 4242 4242 4242 4242
        </p>
      </Card>

      <Card title="Payment history" icon={Receipt} testId="profile-billing-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-600">
            Every charge raised on this account, newest first.
          </p>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
              Total paid
            </div>
            <div
              className="font-display text-xl font-black text-slate-900"
              data-testid="profile-total-paid"
            >
              ${(billing.total_paid || 0).toFixed(2)}
            </div>
          </div>
        </div>
        {billing.charges.length === 0 ? (
          <p
            className="mt-4 rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500"
            data-testid="profile-billing-empty"
          >
            No charges yet. Choose a plan above to activate your subscription.
          </p>
        ) : (
          <div className="mt-4 space-y-2">
            {billing.charges.map((c) => (
              <div
                key={c.session_id}
                data-testid="profile-charge-row"
                className="flex items-center justify-between gap-4 rounded-md border border-slate-200 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{c.plan_name}</div>
                  <div className="mt-0.5 text-xs text-slate-500">{fmtDate(c.created_at)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-mono text-sm text-slate-800">
                    ${c.amount.toFixed(2)} {c.currency}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      STATUS_STYLE[c.status] || "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {c.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <form onSubmit={save}>
        <Card title="Vendor/Supplier details & branding" icon={Building2} testId="profile-details-card">
          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map(([key, label, type, required]) => (
              <div key={key}>
                <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {label}
                </Label>
                <Input
                  type={type}
                  required={required}
                  value={form[key]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [key]: type === "tel" ? formatPhone(e.target.value) : e.target.value,
                    })
                  }
                  data-testid={`profile-${key}-input`}
                  className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Business address
              </Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={3}
                data-testid="profile-address-input"
                className="mt-2 border-slate-300 focus-visible:ring-blue-600"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="profile-save-button"
            className="mt-6 h-11 rounded-full bg-blue-600 px-7 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
          >
            <Save className="mr-2 h-4 w-4" /> {busy ? "Saving…" : "Save profile"}
          </Button>
        </Card>
      </form>

      <ChangePasswordCard />
    </div>
  );
};
